import {CommonModule} from '@angular/common';
import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ChartConfiguration, ChartOptions} from 'chart.js';
import {BaseChartDirective} from 'ng2-charts';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faAngleLeft, faAngleRight, faClock, faEye, faXmark} from '@fortawesome/free-solid-svg-icons';
import {FormsModule} from '@angular/forms';
import {ApiService} from '../services/api.service';
import {ToastrService} from 'ngx-toastr';
import {AuthService} from '../services/auth.service';
import {Chart} from 'chart.js/auto';
import {finalize, Subject, takeUntil} from 'rxjs';
import {ImageViewerComponent} from '../shared/image-viewer/image-viewer.component';
import {VehicleNumberCopyDirective} from '../shared/vehicle-number-copy/vehicle-number-copy.directive';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../shared/constants/pagination.constants';


@Component({
    selector: 'app-analytics',
    imports: [CommonModule, FormsModule, BaseChartDirective, FontAwesomeModule, ImageViewerComponent, VehicleNumberCopyDirective],
    templateUrl: './analytics.component.html',
    styleUrls: ['./analytics.component.css']
})
export class AnalyticsComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;

  camera_esn = [];
  timeRanges = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: '7days' },
    { label: '1 Month', value: 'month' },
  ];
  selectedRange = '7days';
    rtoDirectionMode = 'ENTRY';
  showRtoDetails = false;
  rtoDetailRows: Array<{ label: string; count: number }> = [];
  stateCards: Array<{
    key: string;
    label: string;
    count: number;
    description: string;
    severity: string;
    icon: string;
    className: string
  }> = [];
  occupancy = {
    capacity: 0,
    occupied: 0,
    available: 0,
    occupancy_percent: 0,
  };
  staleUnmatched = {
    count: 0,
    threshold_hours: 24,
  };
  staleDetailsExpanded = false;
  staleLoading = false;
  staleRows: Array<{
    vehicle_number: string;
    entry_time: string;
    camera_esn: string;
    stale_hours: number;
    entry_vehicle_image_url?: string;
  }> = [];
  staleCurrentPage = 1;
  staleRecordsPerPage = 10;
  staleTotalRecords = 0;
  staleNextPage: number | null = null;
  stalePreviousPage: number | null = null;
  selectedImages: { url: string; direction: 'Entry' | 'Exit' }[] = [];
  showImageModal = false;

  private destroy$ = new Subject<void>();
  private statCharts: Record<string, Chart> = {};
  private pendingStaleScroll = false;
  private staleScrollAttempts = 0;
  private readonly maxStaleScrollAttempts = 30;

  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;
  @ViewChild('staleSection') staleSection?: ElementRef<HTMLElement>;

  constructor(
    library: FaIconLibrary,
    private apiService: ApiService,
    private authService: AuthService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.camera_esn = this.authService.getCurrentUserInfo()['camera_esn'];
    library.addIcons(faClock, faXmark, faEye, faAngleLeft, faAngleRight);
  }

  ngOnInit() {
    this.destroy$ = new Subject<void>();
    this.route.fragment.pipe(takeUntil(this.destroy$)).subscribe((fragment) => {
      if (fragment === 'stale-unmatched') {
        this.pendingStaleScroll = true;
        this.openStaleDetails(false);
      }
    });
    const initialFragment = this.route.snapshot.fragment;
    if (initialFragment === 'stale-unmatched') {
      this.pendingStaleScroll = true;
      this.openStaleDetails(false);
    }
    this.donutChart(this.selectedRange);
  }

  ngAfterViewInit() {
    this.refreshStatCharts();
    this.scheduleStaleSectionScroll();
  }

  ngOnDestroy() {
    Object.values(this.statCharts).forEach((chart) => chart.destroy());
    this.statCharts = {};
    this.destroy$.next();
    this.destroy$.complete();
  }

  donutChartData: ChartConfiguration<'doughnut'>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [
          '#4a90e2', '#7ed321', '#f8e71c', '#bd10e0', '#d0021b', '#50e3c2', '#9b9b9b'
        ],
        hoverOffset: 6
      }
    ]
  };

  donutChartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#333'
        }
      }
    }
  };

  donutChart(range: string) {
    this.selectedRange = range;
    const data: any = {
      camera_esn: this.camera_esn,
      time_range: range,
    };
    this.apiService.analytics(data).pipe(
      takeUntil(this.destroy$),
    ).subscribe({
      next: (res: any) => {
        const rtoData = res['rto_data'];
        const vehicleStats = res?.vehicle_stats || {};

        this.donutChartData.labels = rtoData?.labels || [];
        this.donutChartData.datasets[0].data = rtoData?.data || [];
          this.rtoDirectionMode = res?.direction_mode || 'ENTRY';
        const detailLabels = res?.rto_detail_data?.labels || [];
        const detailData = res?.rto_detail_data?.data || [];
        this.rtoDetailRows = detailLabels.map((label: string, index: number) => ({
          label,
          count: Number(detailData[index] || 0),
        })).filter((row: any) => row.label !== 'Other');
        this.applyOccupancyData(res?.occupancy);
        this.applyStaleUnmatched(res?.stale_unmatched);
        this.applyConnectionStates(res?.connection_states);

        this.stats1 = this.stats1.map((stat) => {
          if (stat.title === 'Last One Hour') {
            return {
              ...stat,
              value: vehicleStats?.last_one_hour ?? 0,
              change: vehicleStats?.last_one_hour_change ?? 0,
              changeLabel: vehicleStats?.last_one_hour_label ?? 'previous hour',
            };
          }
          if (stat.title === 'Today') {
            return {
              ...stat,
              value: vehicleStats?.today ?? 0,
              change: vehicleStats?.today_change ?? 0,
              changeLabel: vehicleStats?.today_label ?? 'yesterday',
            };
          }
          if (stat.title === 'Last Week') {
            return {
              ...stat,
              value: vehicleStats?.last_week ?? 0,
              change: vehicleStats?.last_week_change ?? 0,
              changeLabel: vehicleStats?.last_week_label ?? 'last week',
            };
          }
          if (stat.title === 'Last Month') {
            return {
              ...stat,
              value: vehicleStats?.last_month ?? 0,
              change: vehicleStats?.last_month_change ?? 0,
              changeLabel: vehicleStats?.last_month_label ?? 'last month',
            };
          }
          return stat;
        });

        this.refreshStatCharts();

        if (this.chart) {
          this.chart.update();
        }

        if (this.staleDetailsExpanded) {
          this.loadStalePage(this.staleCurrentPage);
        } else if (this.pendingStaleScroll) {
          this.scheduleStaleSectionScroll();
        }
      },
      error: (err: any) => {
        if (err['error']?.['non_field_errors']) {
          err['error']['non_field_errors'].forEach((element: any) => {
            this.toastr.error(element);
          });
        } else if (err['status'] !== 403 && err.status !== 0) {
          this.toastr.error('Something went wrong!.', 'Error');
        }
      }
    });
  }

  private applyStaleUnmatched(stale: any) {
    this.staleUnmatched = {
      count: Number(stale?.count || 0),
      threshold_hours: Number(stale?.threshold_hours || 24),
    };
    if (!this.staleUnmatched.count) {
      this.staleDetailsExpanded = false;
      this.staleRows = [];
    }
  }

  openStaleDetails(scrollIntoView = false) {
    if (!this.staleUnmatched.count) {
      return;
    }
    if (scrollIntoView) {
      this.pendingStaleScroll = true;
    }
    this.staleDetailsExpanded = true;
    this.staleCurrentPage = 1;
    this.loadStalePage(1);
    this.scheduleStaleSectionScroll();
  }

  closeStaleDetails() {
    this.staleDetailsExpanded = false;
    void this.router.navigate([], {
      relativeTo: this.route,
      fragment: undefined,
      replaceUrl: true,
    });
  }

  private scheduleStaleSectionScroll() {
    if (!this.pendingStaleScroll) {
      return;
    }
    this.staleScrollAttempts = 0;
    this.tryScrollToStaleSection();
  }

  private tryScrollToStaleSection() {
    if (!this.pendingStaleScroll) {
      return;
    }
    const element = this.staleSection?.nativeElement;
    if (!element) {
      if (this.staleScrollAttempts < this.maxStaleScrollAttempts) {
        this.staleScrollAttempts += 1;
        requestAnimationFrame(() => this.tryScrollToStaleSection());
      }
      return;
    }
    this.pendingStaleScroll = false;
    this.staleScrollAttempts = 0;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  loadStalePage(page: number) {
    if (!this.staleUnmatched.count) {
      return;
    }
    this.staleLoading = true;
    const payload = {
      camera_esn: this.camera_esn,
      time_range: this.selectedRange,
      stale_threshold_hours: this.staleUnmatched.threshold_hours,
    };
    this.apiService.getStaleUnmatchedVehicles(payload, page, this.staleRecordsPerPage).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.staleLoading = false;
      })
    ).subscribe({
      next: (res: any) => {
        this.staleRows = (res?.results || []).map((row: any) => ({
          vehicle_number: row?.vehicle_number || '-',
          entry_time: row?.entry_time || '',
          camera_esn: row?.camera_esn || '-',
          stale_hours: Number(row?.stale_hours || 0),
          entry_vehicle_image_url: row?.entry_vehicle_image_url,
        }));
        this.staleTotalRecords = res?.count ?? this.staleRows.length;
        this.staleNextPage = res?.next ?? null;
        this.stalePreviousPage = res?.previous ?? null;
        this.staleCurrentPage = page;
        if (this.pendingStaleScroll) {
          this.scheduleStaleSectionScroll();
        }
      },
      error: (err: any) => {
        if (err?.status !== 403 && err?.status !== 0) {
          this.toastr.error('Unable to load stale vehicle records.', 'Error');
        }
      }
    });
  }

  staleNext() {
    if (this.staleNextPage) {
      this.loadStalePage(this.staleCurrentPage + 1);
    }
  }

  stalePrevious() {
    if (this.stalePreviousPage) {
      this.loadStalePage(this.staleCurrentPage - 1);
    }
  }

  staleSelectPageSize() {
    this.staleCurrentPage = 1;
    this.loadStalePage(1);
  }

  formatStaleDuration(hours: number): string {
    if (!hours || hours < 1) {
      return '< 1 hour';
    }
    if (hours < 24) {
      return `${Math.round(hours)} hour${Math.round(hours) === 1 ? '' : 's'}`;
    }
    const days = Math.floor(hours / 24);
    const remainder = Math.round(hours % 24);
    if (!remainder) {
      return `${days} day${days === 1 ? '' : 's'}`;
    }
    return `${days} day${days === 1 ? '' : 's'} ${remainder} hour${remainder === 1 ? '' : 's'}`;
  }

  viewStaleImage(imageUrl?: string) {
    if (!imageUrl) {
      return;
    }
    this.selectedImages = [{ url: imageUrl, direction: 'Entry' }];
    this.showImageModal = true;
  }

  closeImageModal = () => {
    this.showImageModal = false;
    this.selectedImages = [];
  };

  private applyOccupancyData(occupancy: any) {
    this.occupancy = {
      capacity: Number(occupancy?.capacity || 0),
      occupied: Number(occupancy?.occupied || 0),
      available: Number(occupancy?.available || 0),
      occupancy_percent: Number(occupancy?.occupancy_percent || 0),
    };
    const zones = occupancy?.zones?.length ? occupancy.zones : [
      {
        name: 'Overall',
        parked: this.occupancy.occupied,
        reserved: 0,
        blocked: 0,
        free: this.occupancy.available,
      }
    ];
    this.barChartData = {
      labels: zones.map((zone: any) => zone.name),
      datasets: [
        { data: zones.map((zone: any) => Number(zone.parked || 0)), label: 'Parked', backgroundColor: '#4a90e2' },
        { data: zones.map((zone: any) => Number(zone.reserved || 0)), label: 'Reserved', backgroundColor: '#7ed321' },
        { data: zones.map((zone: any) => Number(zone.blocked || 0)), label: 'Blocked', backgroundColor: '#f8e71c' },
        { data: zones.map((zone: any) => Number(zone.free || 0)), label: 'Free', backgroundColor: '#9b9b9b' }
      ]
    };
  }

  private applyConnectionStates(states: any[] = []) {
    const presentation: Record<string, { icon: string; className: string }> = {
      camera_offline: {icon: '!', className: 'state-offline'},
      device_disconnected: {icon: 'x', className: 'state-disconnected'},
      no_signal: {icon: '~', className: 'state-signal'},
      maintenance: {icon: '*', className: 'state-maintenance'},
      reconnecting: {icon: '↻', className: 'state-reconnecting'},
      service_unavailable: {icon: 'i', className: 'state-unavailable'},
    };

    this.stateCards = (states || []).map((state) => {
      const card = presentation[state.key] || {icon: 'i', className: 'state-unavailable'};
      return {
        key: state.key,
        label: state.label,
        count: Number(state.count || 0),
        description: state.description,
        severity: state.severity,
        icon: card.icon,
        className: card.className,
      };
    });
  }

  openRtoDetails() {
    this.showRtoDetails = true;
  }

  closeRtoDetails() {
    this.showRtoDetails = false;
  }

    getRtoDirectionLabel(mode: string): string {
        switch (mode) {
            case 'EXIT':
                return 'Exit only';
            case 'ENTRY_EXIT':
                return 'Entry & Exit';
            default:
                return 'Entry only';
        }
    }

  barChartData: ChartConfiguration<'bar'>['data'] = {
    labels: ['Overall'],
    datasets: [
      { data: [0], label: 'Parked', backgroundColor: '#4a90e2' },
      { data: [0], label: 'Reserved', backgroundColor: '#7ed321' },
      { data: [0], label: 'Blocked', backgroundColor: '#f8e71c' },
      { data: [0], label: 'Free', backgroundColor: '#9b9b9b' }
    ]
  };

  barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true
      }
    },
    plugins: {
      legend: {
        position: 'bottom'
      }
    }
  };

  stats1: Array<any> = [
    { id: 'chart1', title: 'Last One Hour', value: 0, change: 0, changeLabel: 'previous hour' },
    { id: 'chart2', title: 'Today', value: 0, change: 0, changeLabel: 'yesterday' },
    { id: 'chart3', title: 'Last Week', value: 0, change: 0, changeLabel: 'last week' },
    { id: 'chart4', title: 'Last Month', value: 0, change: 0, changeLabel: 'last month' },
  ];

  refreshStatCharts() {
    setTimeout(() => {
      this.stats1.forEach(stat => this.createChart(stat));
    }, 0);
  }

  trackByStat(index: number, stat: any) {
    return stat?.id || index;
  }

  createChart(stat: any) {
    const ctx = document.getElementById(stat.id) as HTMLCanvasElement;
    if (!ctx) {
      return;
    }

    const existing = this.statCharts[stat.id];
    if (existing) {
      existing.destroy();
    }

    const isIncrease = stat.change >= 0;
    const lineColor = isIncrease ? '#3b82f6' : '#e74c3c';
    const fillColor = isIncrease ? 'rgba(59,130,246,0.1)' : 'rgba(231,76,60,0.1)';
    const dataPoints = isIncrease ? [9, 12, 11, 14, 12, 15] : [16, 13, 15, 13, 17, 11];

    this.statCharts[stat.id] = new Chart(ctx, {
      type: 'line',
      data: {
        labels: ['', '', '', '', '', ''],
        datasets: [{
          data: dataPoints,
          borderColor: lineColor,
          backgroundColor: fillColor,
          borderWidth: 2,
          tension: 0.4,
          fill: true,
          pointRadius: 0,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: { x: { display: false }, y: { display: false } },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }
        }
      }
    });
  }
}
